import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  useRef,
  useEffect,
} from 'react';
import { useApiGateway } from 'hooks/useApi';

interface AbbreviatedEarnings {
  t: string; // symbol
  d: string; // date
  epsA: number | null; // epsActual
  epsE: number | null; // epsEstimated
  revA: number | null; // revenueActual
  revE: number | null; // revenueEstimated
  lu: string; // lastUpdated
}

export interface EarningsContextType {
  earningsMaps: {
    earnings?: Record<string, AbbreviatedEarnings>;
    from?: string;
    to?: string;
    lastUpdated?: string;
  };
  error: string | null;
  isLoading: boolean;
  fetchEarnings: (force?: boolean) => Promise<void>;
  lastFetchTime: number | null;
  clearCache: () => void;
}

export const EarningsContext = createContext<EarningsContextType | undefined>(undefined);

interface EarningsProviderProps {
  children: ReactNode;
  refreshInterval?: number;
  maxCacheSize?: number;
}

export function EarningsProvider({
  children,
  refreshInterval = 5 * 60 * 1000,
  maxCacheSize = Infinity,
}: EarningsProviderProps) {
  const [earningsMaps, setEarningsMaps] = useState<{
    earnings?: Record<string, AbbreviatedEarnings>;
    from?: string;
    to?: string;
    lastUpdated?: string;
  }>({});
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);

  const activeRequestRef = useRef<Promise<void> | null>(null);
  const isMountedRef = useRef(true);

  const { get: fetchEarningsMaps, isLoading } = useApiGateway('/v1/products/option-flow/earnings');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      activeRequestRef.current = null;
    };
  }, []);

  const fetchEarnings = useCallback(
    async (force = false) => {
      if (!force && lastFetchTime && Date.now() - lastFetchTime < refreshInterval) {
        return;
      }
      if (activeRequestRef.current && !force) {
        return activeRequestRef.current;
      }

      const requestPromise = (async () => {
        try {
          if (!isMountedRef.current) return;

          setError(null);
          const resp = await fetchEarningsMaps();

          if (!isMountedRef.current) return;

          const data = resp?.data || {};

          if (Object.keys(data).length > maxCacheSize) {
            console.warn('Earnings data exceeds maxCacheSize, truncating...');
            const truncatedData = Object.fromEntries(Object.entries(data).slice(0, maxCacheSize));
            setEarningsMaps(truncatedData);
          } else {
            setEarningsMaps(data);
          }

          setLastFetchTime(Date.now());
        } catch (err) {
          if (!isMountedRef.current) return;

          console.error('Failed to fetch earnings:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch earnings');
        }
      })();

      activeRequestRef.current = requestPromise;
      await requestPromise;
      activeRequestRef.current = null;
    },
    [fetchEarningsMaps, lastFetchTime, refreshInterval, maxCacheSize],
  );

  const clearCache = useCallback(() => {
    setEarningsMaps({});
    setError(null);
    setLastFetchTime(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      earningsMaps,
      error,
      isLoading,
      fetchEarnings,
      lastFetchTime,
      clearCache,
    }),
    [earningsMaps, error, isLoading, fetchEarnings, lastFetchTime, clearCache],
  );

  return <EarningsContext.Provider value={contextValue}>{children}</EarningsContext.Provider>;
}
